import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ApperIcon from "@/components/ApperIcon";
import Button from "@/components/atoms/Button";
import Input from "@/components/atoms/Input";
import { toast } from "react-toastify";

const AddClientModal = ({ isOpen, onClose, onSubmit }) => {
const [formData, setFormData] = useState({
    companyName: "",
    clientInformation: "",
    websiteUrl: "",
    hiddenLink: "",
    brandVoice: {
      profileSummary: "",
      keywords: "",
      hashtags: "",
      voice: "",
      targetAudience: "",
      description: "",
      benefits: "",
      audiences: [],
      campaignObjective: "",
      offerCategory: "",
      brandToneVoice: "",
      topCustomerDesires: "",
      primaryCustomerProblems: "",
      proofAssets: ""
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);


const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Truncate hidden link to 255 characters to prevent API errors
    const processedValue = name === 'hiddenLink' && value.length > 255 ? value.substring(0, 255) : value;
    
    if (name.startsWith('brandVoice.')) {
      const brandVoiceField = name.replace('brandVoice.', '');
      setFormData(prev => ({
        ...prev,
        brandVoice: {
          ...prev.brandVoice,
          [brandVoiceField]: processedValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
if (!formData.companyName) {
      toast.error("Please fill in the company name");
      return;
    }

    if (formData.hiddenLink && formData.hiddenLink.length > 255) {
      toast.error("Hidden Link must be 255 characters or less");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      
      // Reset form
setFormData({
        companyName: "",
        clientInformation: "",
        websiteUrl: "",
        hiddenLink: "",
        brandVoice: {
          profileSummary: "",
          keywords: "",
          hashtags: "",
          voice: "",
          targetAudience: "",
          description: "",
          benefits: "",
          audiences: [],
          campaignObjective: "",
          offerCategory: "",
          brandToneVoice: "",
          topCustomerDesires: "",
          primaryCustomerProblems: "",
          proofAssets: ""
        }
      });
      
      onClose();
    } catch (error) {
      toast.error("Failed to add client. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          onClick={handleBackdropClick}
        >
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-facebook to-facebook-hover rounded-lg p-2">
                    <ApperIcon name="Building2" className="w-5 h-5 text-white" />
                  </div>
<div>
                    <h2 className="text-lg font-semibold text-gray-900">Add New Client</h2>
                    <p className="text-sm text-gray-600">Enter client information</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1"
                >
                  <ApperIcon name="X" className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
<Input
                  name="companyName"
                  label="Company Name *"
                  placeholder="Enter company name"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                />

                <Input
                  name="websiteUrl"
                  label="Website URL"
                  placeholder="https://example.com"
                  value={formData.websiteUrl}
                  onChange={handleInputChange}
/>

<div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hidden Link {formData.hiddenLink.length > 0 && (
                      <span className={`text-xs ${
                        formData.hiddenLink.length >= 240 
                          ? formData.hiddenLink.length === 255 
                            ? 'text-orange-500' 
                            : 'text-gray-400'
                          : 'text-gray-400'
                      }`}>
                        ({formData.hiddenLink.length}/255)
                      </span>
                    )}
                  </label>
                  <Input
                    name="hiddenLink"
                    placeholder="Enter hidden link (max 255 characters)"
                    value={formData.hiddenLink}
                    onChange={handleInputChange}
                    className={formData.hiddenLink.length >= 240 ? 'border-orange-300 focus:border-orange-500 focus:ring-orange-500' : ''}
                    maxLength={255}
                  />
                  {formData.hiddenLink.length >= 240 && formData.hiddenLink.length < 255 && (
                    <p className="text-orange-500 text-xs mt-1">
                      Approaching character limit ({255 - formData.hiddenLink.length} characters remaining)
                    </p>
                  )}
                  {formData.hiddenLink.length === 255 && (
                    <p className="text-orange-600 text-xs mt-1 font-medium">
                      Character limit reached (255/255)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Information
                  </label>
                  <textarea
                    name="clientInformation"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-facebook focus:border-facebook resize-none"
                    placeholder="Enter any relevant information about this client (contact details, industry, goals, budget, notes, etc.)"
                    value={formData.clientInformation}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Brand Voice Section */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Brand Voice Profile</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Profile Summary
                      </label>
                      <textarea
                        name="brandVoice.profileSummary"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-facebook focus:border-facebook resize-none text-sm"
                        placeholder="Brief overview of brand voice and personality"
                        value={formData.brandVoice?.profileSummary || ""}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Keywords
                        </label>
                        <textarea
                          name="brandVoice.keywords"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-facebook focus:border-facebook resize-none text-sm"
                          placeholder="Key brand terms and phrases"
                          value={formData.brandVoice?.keywords || ""}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hashtags
                        </label>
                        <textarea
                          name="brandVoice.hashtags"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-facebook focus:border-facebook resize-none text-sm"
                          placeholder="Brand hashtags and social tags"
                          value={formData.brandVoice?.hashtags || ""}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Brand Voice & Tone
                      </label>
                      <textarea
                        name="brandVoice.voice"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-facebook focus:border-facebook resize-none text-sm"
                        placeholder="How the brand communicates (friendly, professional, casual, etc.)"
                        value={formData.brandVoice?.voice || ""}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Target Audience
                      </label>
                      <textarea
                        name="brandVoice.targetAudience"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-facebook focus:border-facebook resize-none text-sm"
                        placeholder="Primary audience demographics and characteristics"
                        value={formData.brandVoice?.targetAudience || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? (
                      <>
                        <ApperIcon name="Loader2" className="w-4 h-4 animate-spin mr-2" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <ApperIcon name="Plus" className="w-4 h-4 mr-2" />
                        Add Client
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddClientModal;